import React, { useState, useRef, useEffect } from 'react';

const ResizableBlock = ({
  children,
  initialHeight = 200,
  initialWidth = 600,
}) => {
  const [height, setHeight] = useState(initialHeight);
  const [width, setWidth] = useState(initialWidth);
  const [leftOffset, setLeftOffset] = useState(0);
  const [resizeMode, setResizeMode] = useState(null);
  const [startY, setStartY] = useState(0);
  const [startX, setStartX] = useState(0);
  const [startHeight, setStartHeight] = useState(initialHeight);
  const [startWidth, setStartWidth] = useState(initialWidth);
  const [startLeft, setStartLeft] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizeMode) return;

      if (resizeMode === 'vertical') {
        const deltaY = e.clientY - startY;
        setHeight(Math.max(100, startHeight + deltaY));
        return;
      }

      if (resizeMode === 'horizontal') {
        const deltaX = e.clientX - startX;
        const nextWidth = Math.max(200, startWidth + deltaX);
        const nextLeft = startLeft - (nextWidth - startWidth) / 2;
        setWidth(nextWidth);
        setLeftOffset(nextLeft);
      }
    };

    const handleMouseUp = () => {
      setResizeMode(null);
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
    };
  }, [resizeMode, startX, startY, startHeight, startWidth]);

  const startResize = (mode, e) => {
    e.preventDefault();
    setResizeMode(mode);
    document.body.style.userSelect = 'none';

    if (mode === 'vertical') {
      setStartY(e.clientY);
      setStartHeight(height);
      return;
    }

    const currentLeft = containerRef.current?.offsetLeft || 0;
    setStartX(e.clientX);
    setStartWidth(width);
    setStartLeft(currentLeft);
    setLeftOffset(currentLeft);
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: `${width}px`,
        minWidth: '200px',
        minHeight: '100px',
        height: `${height}px`,
        maxWidth: 'none',
        position: 'relative',
        left: `${leftOffset}px`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: 'white',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 6,
      }}
    >
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {children}
      </div>

      <div
        title="Drag to resize height"
        style={{
          flexShrink: 0,
          width: '100%',
          height: '10px',
          cursor: 'ns-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.04)',
          userSelect: 'none',
          borderTop: '1px solid rgba(0,0,0,0.06)',
        }}
        onMouseDown={(e) => startResize('vertical', e)}
      >
        <div style={{
          width: 32,
          height: 4,
          borderRadius: 2,
          background: 'rgba(0,0,0,0.25)',
        }} />
      </div>

      <div
        title="Drag to resize width"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '10px',
          height: '100%',
          cursor: 'ew-resize',
          background: 'rgba(0,0,0,0.02)',
          borderLeft: '1px solid rgba(0,0,0,0.06)',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onMouseDown={(e) => startResize('horizontal', e)}
      >
        <div style={{
          width: 4,
          height: 32,
          borderRadius: 2,
          background: 'rgba(0,0,0,0.25)',
        }} />
      </div>
    </div>
  );
};

export default ResizableBlock;
